import {
  Box,
  Button,
  Drawer,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { useGetBranches, useGetPaymentMethods } from "src/api/generic";
import FilterIcon from "src/assets/svg-icons/aggregators/filter.svg";
import { useTranslation } from "react-i18next";
import { useGetOCAccountsList } from "src/api/operation-cost/accounts";
import { specificBranchHasPermission } from "src/utils";
import { OCPermissions } from "src/constants/permissions";
import { useState } from "react";
import { useGetVendorsList } from "src/api/operation-cost/vendors";

interface IProps {
  open: boolean;
  handleClose: any;
  refNum: string;
  setRefNum: Function;
  paymentId: string;
  setPaymentId: Function;
  accountId: string;
  setAccountId: Function;
  vendorId: string;
  setVendorId: Function;
  status: string;
  setStatus: Function;
  type: string;
  setType: Function;
  branchId: string;
  setBranchId: Function;
  totalWithTax: string;
  setTotalWithTax: Function;
  isAssetsOnly?: boolean;
}
const FilterDrawer = (props: IProps) => {
  const {
    open,
    handleClose,
    refNum,
    setRefNum,
    paymentId,
    setPaymentId,
    accountId,
    setAccountId,
    vendorId,
    setVendorId,
    status,
    setStatus,
    type,
    setType,
    branchId,
    setBranchId,
    totalWithTax,
    setTotalWithTax,
    isAssetsOnly,
  } = props;
  const { t } = useTranslation("purchasing");
  const { t: ocT } = useTranslation("operationCost");
  const { t: generalT } = useTranslation("general");
  const [tempRefNum, setTempRefNum] = useState(refNum);
  const [tempPaymentId, setTempPaymentId] = useState(paymentId);
  const [tempAccountId, setTempAccountId] = useState(accountId);
  const [tempVendorId, setTempVendorId] = useState(vendorId);
  const [tempTotal, setTempTotal] = useState(totalWithTax);
  const [tempStatus, setTempStatus] = useState(status);
  const [tempBranchId, setTempBranchId] = useState(branchId);
  const [tempType, setTempType] = useState(status);

  // APIS
  const { data: paymentsData } = useGetPaymentMethods();
  const { data: accountsData } = useGetOCAccountsList({
    ...(isAssetsOnly ? { is_asset: 1 } : {}),
  });
  const { data: branchesData } = useGetBranches();
  const { data: vendorsData } = useGetVendorsList();

  // permissions
  const branchesHavePermission =
    branchesData?.data.filter((branch) =>
      specificBranchHasPermission(OCPermissions.viewOCList, branch.id),
    ) || [];

  const handleApply = () => {
    handleClose();
    setRefNum(tempRefNum);
    setPaymentId(tempPaymentId);
    setVendorId(tempVendorId);
    setAccountId(tempAccountId);
    setStatus(tempStatus);
    setTotalWithTax(tempTotal);
    setBranchId(tempBranchId);
    setType(tempType);
  };

  const handleClearFilters = () => {
    handleClose();
    setRefNum("");
    setPaymentId();
    setVendorId();
    setAccountId();
    setStatus("all");
    setType("all");
    setBranchId();
    setTotalWithTax("");

    setTempRefNum("");
    setTempPaymentId("");
    setTempVendorId("");
    setTempAccountId("");
    setTempStatus("all");
    setTempType("all");
    setTempBranchId("");
    setTempTotal("");
  };

  const types = [
    {
      label: generalT("all"),
      value: "all",
    },
    {
      label: ocT("fixedCost"),
      value: "fixed",
    },
    {
      label: ocT("variable"),
      value: "variable",
    },
    {
      label: ocT("asset"),
      value: "asset",
    },
  ];

  const statusTypes = [
    {
      label: generalT("all"),
      value: "all",
    },
    {
      label: ocT("paid"),
      value: "paid",
    },
    {
      label: ocT("unpaid"),
      value: "unpaid",
    },
    {
      label: ocT("partiallyPaid"),
      value: "partial_paid",
    },
    {
      label: ocT("reminder"),
      value: "reminder",
    },
  ];

  return (
    <Drawer anchor={"right"} open={open} onClose={handleClose}>
      <Stack
        maxWidth={{ xs: "100%", sm: "376px" }}
        minWidth={{ xs: "100%", sm: "376px" }}
        borderRight={"1px solid #D0D5DD"}
        height={"100%"}
        maxHeight={"100vh"}
        top={"0"}
      >
        <Box
          display={"flex"}
          justifyContent={"space-between"}
          p={"20px 27px"}
          bgcolor={"#fff"}
          boxShadow="0px 0px 40px 0px rgba(11, 24, 42, 0.07)"
        >
          <Button
            color="tertiary"
            variant="text"
            size="small"
            endIcon={<img src={FilterIcon} height={"16px"} alt="" />}
          >
            {t("filter")}
          </Button>
          <Button
            variant="outlined"
            color="tertiary"
            size="small"
            onClick={handleClose}
          >
            {generalT("hide")}
          </Button>
        </Box>
        <Stack spacing={"20px"} p={"40px 27px"}>
          <TextField
            label={t("reference")}
            variant="outlined"
            size="small"
            fullWidth
            value={tempRefNum}
            onChange={(e) => setTempRefNum(e.target.value)}
          />
          <TextField
            type="number"
            label={generalT("totalWithTax")}
            variant="outlined"
            size="small"
            fullWidth
            value={tempTotal}
            onChange={(e) => setTempTotal(e.target.value)}
          />
          {/*  type */}
          <FormControl fullWidth size="small">
            <InputLabel id="demo-multiple-checkbox-label">
              {t("type")}
            </InputLabel>
            <Select
              labelId="demo-multiple-checkbox-label"
              id="demo-multiple-checkbox"
              value={tempType}
              onChange={(e) => setTempType(e.target.value)}
              input={<OutlinedInput label={t("type")} />}
            >
              {types.map(({ label, value }) => (
                <MenuItem key={value} value={value}>
                  <ListItemText primary={label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {/*  status */}
          <FormControl fullWidth size="small">
            <InputLabel id="demo-multiple-checkbox-label">
              {t("status")}
            </InputLabel>
            <Select
              labelId="demo-multiple-checkbox-label"
              id="demo-multiple-checkbox"
              value={tempStatus}
              onChange={(e) => setTempStatus(e.target.value)}
              input={<OutlinedInput label={t("status")} />}
            >
              {statusTypes.map(({ label, value }) => (
                <MenuItem key={value} value={value}>
                  <ListItemText primary={label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* <TextField
            label={t("billNumber")}
            variant="outlined"
            size="small"
            fullWidth
            value={invoiceNum}
            onChange={(e) => setInvoiceNum(e.target.value)}
          /> */}
          {/*  payment Method  Select */}
          <FormControl fullWidth size="small">
            <InputLabel id="demo-multiple-checkbox-label">
              {t("paymentMethod")}
            </InputLabel>
            <Select
              labelId="demo-multiple-checkbox-label"
              id="demo-multiple-checkbox"
              value={tempPaymentId}
              onChange={(e) => setTempPaymentId(e.target.value)}
              input={<OutlinedInput label={t("paymentMethod")} />}
            >
              {paymentsData?.data?.map((payment: any) => (
                <MenuItem key={payment.id} value={payment.id}>
                  <ListItemText primary={payment.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {/*  Vendor Select */}
          <FormControl fullWidth size="small">
            <InputLabel id="demo-multiple-checkbox-label">
              {generalT("vendor")}
            </InputLabel>
            <Select
              labelId="demo-multiple-checkbox-label"
              id="demo-multiple-checkbox"
              value={tempVendorId}
              onChange={(e) => setTempVendorId(e.target.value)}
              input={<OutlinedInput label={generalT("vendor")} />}
            >
              {vendorsData?.data
                ?.map((vendor: any) => (
                  <MenuItem key={vendor.id} value={vendor.id}>
                    <ListItemText primary={vendor.name} />
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          {/*  Account Select */}
          <FormControl fullWidth size="small">
            <InputLabel id="demo-multiple-checkbox-label">
              {ocT("account")}
            </InputLabel>
            <Select
              labelId="demo-multiple-checkbox-label"
              id="demo-multiple-checkbox"
              value={tempAccountId}
              onChange={(e) => setTempAccountId(e.target.value)}
              input={<OutlinedInput label={ocT("account")} />}
            >
              {accountsData?.data
                ?.filter((account: any) => account.status)
                ?.map((account: any) => (
                  <MenuItem key={account.id} value={account.id}>
                    <ListItemText primary={account.name} />
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          {/*  Branch Select */}
          <FormControl fullWidth size="small">
            <InputLabel id="demo-multiple-checkbox-label">
              {generalT("branch")}
            </InputLabel>
            <Select
              labelId="demo-multiple-checkbox-label"
              id="demo-multiple-checkbox"
              value={tempBranchId}
              onChange={(e) => setTempBranchId(e.target.value)}
              input={<OutlinedInput label={generalT("branch")} />}
            >
              {branchesHavePermission?.map((branch: any) => (
                <MenuItem key={branch.id} value={branch.id}>
                  <ListItemText primary={branch.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* divider */}
          <Box width={"100%"} height={"1px"} bgcolor={"#CBD5E1"} />
        </Stack>
        <Box
          display={"flex"}
          justifyContent={"space-between"}
          p={"20px 27px"}
          bgcolor={"#fff"}
          boxShadow="0px 0px 40px 0px rgba(11, 24, 42, 0.07)"
          alignItems={"flex-end"}
          mt="auto"
        >
          <Button variant="contained" size="small" onClick={handleApply}>
            {generalT("apply")}
          </Button>
          <Button
            variant="outlined"
            color="tertiary"
            size="small"
            onClick={handleClearFilters}
          >
            {generalT("clear")}
          </Button>
        </Box>
      </Stack>
    </Drawer>
  );
};
export default FilterDrawer;
